import { Alert, AlertIcon, Box } from "@chakra-ui/react";
import { useMemo } from "react";
import { Loader } from "@tether-web-portal/components/loading/Loader";
import DateRangeControls, { maxReadingDays } from "@tether-web-portal/components/property/DateRangeControls";
import { AreaPageHeader } from "@tether-web-portal/components/areas/AreaPageHeader";
import { AreaType, isAreaTypeParam, useAreaPageData } from "@tether-web-portal/hooks/useAreaPageData";
import { useGetCircuitNodeTree } from "@tether-web-portal/api/properties/hooks";
import AreaPageStatusCard from "@tether-web-portal/components/areas/AreaPageStatusCard";
import { Navigate, useParams } from "react-router-dom";
import { OccupancyDashboard } from "@tether-web-portal/components/occupancy/OccupancyDashboard";
import { IOccupancyPageFilters, OccupancyFilterType } from "@tether-web-portal/web-workers/occupancy";
import { PropertyProvider } from "@tether-web-portal/contexts/PropertyContext";

const LocationDetailOccupancyPageContent = ({ id, areaType, loading, error, area, comfortScore, property, propertyInfo }:
  { id: string } & ReturnType<typeof useAreaPageData>
) => {

  const { circuitNodeTree } = useGetCircuitNodeTree(property?.id);

  const pageFilters = useMemo(
    (): IOccupancyPageFilters => ({
      spaceType: OccupancyFilterType.ROOM,
      floorIndex: null,
      spaceId: id,
    }),
    [id]
  );

  if (loading) {
    return <Loader mt={10} />;
  }
  if (error || !area || !property || !propertyInfo) {
    return (
      <Box>
        <Alert status="error">
          <AlertIcon />
          There was a problem loading data for this area, please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <AreaPageHeader
      area={area}
      areaType={areaType}
      comfortScore={comfortScore}
      property={property}
      propertyInfo={propertyInfo}
      circuitNodeTree={circuitNodeTree ? circuitNodeTree : []}
      dateRangeControl={<DateRangeControls maxSelectedDays={maxReadingDays} />}
      activeTab="occupancy"
    >
      {/* Date range spacer */}
      <Box mb="90px"></Box>

      <AreaPageStatusCard areaType={areaType} id={id} />

      <Box p={5}>
        <OccupancyDashboard propertyId={property.id} pageFilters={pageFilters} />
      </Box>
    </AreaPageHeader>
  );
};

const LocationDetailOccupancyPage = () => {
  const { id = "", areaType = "" } = useParams<{ id: string; areaType: string }>();
  const areaPageData = useAreaPageData(areaType as AreaType, id);
  const propertyId = areaPageData.property?.id || "";

  if (!isAreaTypeParam(areaType)) {
    return <Navigate to="/property" />;
  }

  return (
    <PropertyProvider propertyId={propertyId}>
      <LocationDetailOccupancyPageContent id={id} {...areaPageData} />
    </PropertyProvider>
  );
};

export default LocationDetailOccupancyPage;
